// OFM-250: 이메일 목록 preview 의 EUC-KR 이중 인코딩(mojibake) 표시 보정.
//
// 한국형 Outlook 등이 EUC-KR 원문을 latin-1 로 오독한 채 회신하면, text/plain 파트가
// charset="utf-8" 로 선언됐지만 실제 바이트는 "EUC-KR → latin-1 → UTF-8" 로 이중 오염된다.
// JMAP 서버는 선언대로 UTF-8 디코딩해 preview 를 만들므로 목록 preview 가 깨진다.
// (예: "¾È³çÇÏ¼¼¿ä" 가 실제로는 "안녕하세요")
//
// 표시 계층에서 preview 문자열만 선택 보정한다. Latin-1 보충영역(U+0080–U+00FF) 연속 구간을
// EUC-KR 로 되돌리고, 정상 UTF-8 한글/ASCII/유럽어/장식은 미변형한다. 실패 시 원문 유지(fail-safe).

// legacy 인코딩 미지원 런타임(RN Hermes 등) 방어. 실디코딩까지 확인해 라벨만 받는 런타임도 차단.
let EUC_KR: TextDecoder | null = null
try {
  const decoder = new TextDecoder('euc-kr', { fatal: true })
  if (decoder.decode(Uint8Array.from([0xbe, 0xc8])) === '안') EUC_KR = decoder
} catch {
  EUC_KR = null
}

// WARNING: 반드시 \u 이스케이프. 리터럴 제어문자(U+0080)로 쓰면 번들러/인코딩/편집기 저장에서
//   유실돼 클래스가 붕괴 → 게이트가 조용히 no-op(에러 없이 preview 계속 깨짐)이 된다.
const MOJIBAKE_GATE = /[\u0080-\u00FF]{4,}/
// 어절별 독립 런으로 두면 2음절 어절(≤5자)이 전부 미복원되므로, high-char 런들을 단일 공백으로
// 이어 하나의 세그먼트로 묶는다(공백=EUC-KR 0x20 이라 런 내부로 함께 디코드). 탭/개행 등 다른
// 공백류로는 병합하지 않는다 — 인접한 비한글 high 바이트를 흡수해 오변환할 수 있어서다.
const MOJIBAKE_SEG = /[\u0080-\u00FF]+(?: [\u0080-\u00FF]+)*/g
// 비율 가드 분자를 "한국어 텍스트(한글·자모·한자)"로 넓혀, 한자/기호 섞인 서명
// (예: "홍길동 代表理事 드림")이 세그먼트 전체를 미복원으로 떨어뜨리지 않게 한다.
const KOREAN_TEXT = /[\uAC00-\uD7A3\u1100-\u11FF\u3130-\u318F\u4E00-\u9FFF\uF900-\uFAFF]/g
// 비율 분모에서 제외할 중립 기호(EUC-KR 문장부호/괄호/화살표/도형/원문자/전각기호).
const NEUTRAL_SYMBOL =
  /[\u00B7\u2000-\u206F\u2190-\u21FF\u2460-\u24FF\u2500-\u25FF\u2600-\u26FF\u3000-\u303F\u3200-\u33FF\uFF01-\uFF0F\uFF1A-\uFF20\uFF3B-\uFF40\uFF5B-\uFF65]/g
const HAS_HANGUL = /[가-힣]/
const HANGUL_ONLY = /[가-힣]/g
const SINGLE_REPEAT = /^(.)\1+$/
// 잘린 tail 판정: 세그먼트 뒤가 말줄임표/공백뿐이면 잘린 preview 의 끝으로 본다.
const TRUNCATED_TAIL = /^[\s.…]*$/

/**
 * 이메일 preview 의 EUC-KR 이중 mojibake 구간을 정상 한글로 보정한다.
 * 정상 텍스트/디코딩 실패/미지원 런타임에서는 입력을 그대로 반환한다(멱등, 예외 없음).
 */
export function safePreview(preview: string | null | undefined): string | null | undefined {
  if (!EUC_KR || !preview || !MOJIBAKE_GATE.test(preview)) return preview // fast path
  const end = preview.length
  return preview.replace(MOJIBAKE_SEG, (seg: string, offset: number) => {
    if (seg.split(' ').some((run) => SINGLE_REPEAT.test(run))) return seg // 가드1) 장식 반복 런(····, »»»») 보존
    let target = seg // 가드2) EUC-KR 2바이트 정렬
    let compact = seg.replace(/ /g, '')
    if (compact.length % 2 === 1) {
      if (offset + seg.length !== end) return seg // 중간 홀수 = 노이즈
      target = seg.slice(0, -1) // 256자 잘림 tail: 반쪽 제거
      compact = target.replace(/ /g, '')
    }
    if (compact.length < 4) return seg // 2음절 미만
    const bytes = Uint8Array.from(target, (char) => char.charCodeAt(0))
    let decoded: string
    try {
      decoded = EUC_KR.decode(bytes)
    } catch {
      return seg // 정렬 깨짐 = fail-safe
    }
    const nonSpace = decoded.replace(/ /g, '')
    if (nonSpace.length === 0 || !HAS_HANGUL.test(decoded)) return seg // 가드3) 한글 0자 = 오탐/비한국어 보존
    if (compact.length < 6) {
      // 짧은 세그먼트(2음절)는 잘린 tail(뒤가 말줄임표/공백뿐)이고 순수 한글일 때만 복원.
      // 문장 중간의 심볼 클러스터(예: "¼½¾¿ mixed")가 한글로 오변환되는 것을 막는다.
      if (!TRUNCATED_TAIL.test(preview.slice(offset + seg.length))) return seg
      return (decoded.match(HANGUL_ONLY) || []).length === nonSpace.length ? decoded : seg
    }
    // 가드4) 한국어(한글·한자) 비율 0.7. EUC-KR 문장부호/괄호기호(【】「」※● 등)는 중립으로
    // 분모에서 제외해, 기호가 많은 제목("【공지】 ※필독※ 회의")이 통째로 미복원되는 것을 막는다.
    const korean = (decoded.match(KOREAN_TEXT) || []).length
    const denominator = nonSpace.length - (decoded.match(NEUTRAL_SYMBOL) || []).length
    return denominator > 0 && korean / denominator >= 0.7 ? decoded : seg
  })
}
